import { DiscountType } from './types';
import { formatDiscountAmount } from './formatDiscountAmount';
import { isCouponResponse, isUserReferrerResponse } from './utils/discount';
import type { ReferrerResponse } from './utils/getCoupon';
import { REFERRAL_NOT_FOUND_RESPONSE_DATA } from './utils/getCoupon';

export interface GetDiscountBannerCopyArgs {
  coupon?: ReferrerResponse;
  discountAmount: number;
  discountType: DiscountType;
  isExpired?: boolean;
  overrideCopy?: string;
}

export function getDiscountBannerCopy({
  coupon,
  discountAmount,
  discountType,
  isExpired,
  overrideCopy,
}: GetDiscountBannerCopyArgs): string {
  const discountCopy = formatDiscountAmount({ discountAmount, discountType });
  const isUserReferrer = coupon && isUserReferrerResponse(coupon);
  const referrerName = isUserReferrer ? coupon.name : undefined;

  // Priority 0: Coupon invalid or expired
  if (
    isExpired ||
    coupon === REFERRAL_NOT_FOUND_RESPONSE_DATA ||
    (coupon && typeof coupon === 'object' && coupon.invalid === true)
  ) {
    return `Uh-oh, looks like your referral link is expired but here’s ${discountCopy} because dogs.`;
  }

  // Priority 1: Caller-provided override (e.g., one-week-free, surprise discount, Fi GWP)
  if (overrideCopy) {
    return overrideCopy;
  }

  // Priority 2: CRM / server-side override banner copy
  if (coupon && isCouponResponse(coupon) && coupon.bannerCopy) {
    return coupon.bannerCopy;
  }

  // Priority 3: 100% discount
  if (discountAmount === 100 && discountType === DiscountType.percentage) {
    return isUserReferrer && referrerName
      ? `${referrerName} has given you a free trial!`
      : 'You’ve been given a free trial!';
  }

  // Priority 4: Zero discount
  if (discountAmount === 0) {
    return 'Discover your dog’s vet-recommended meal plan';
  }

  // Priority 5: User referrer with discount amount
  if (isUserReferrer && referrerName) {
    return `${referrerName} has gifted you ${discountCopy} your first purchase!`;
  }

  // Priority 6: Generic fallback
  // TODO: Canonical default copy TBD by product. Using acquisition's current copy as placeholder.
  return `You’ve been gifted ${discountCopy} your first purchase!`;
}
